import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, FluentProvider, Input, MessageBar, MessageBarBody, Spinner, Tab, TabList, Text, Title2, webLightTheme } from "@fluentui/react-components";
import { ArrowSync20Regular, Copy20Regular, Info20Regular, Video20Regular } from "@fluentui/react-icons";
import { codeFromUrl, listMeetings, meetingLink, onMeetingFrame } from "../spurt/meetings";
import type { MeetingView } from "../spurt/meetings";
import { chat, useChat } from "../spurt/chatStore";
import { errText } from "./meetingErrors";
import { badgeOf, copyText, fmtWhen } from "./meetingFormat";
import { openMeeting } from "./meetingUi";
import MeetingDetails from "./MeetingDetails";
import StatusChip from "./StatusChip";
import css from "./Meetings.module.css";

const DAY = 24 * 3600_000;
const PAST_DAYS = 30;
const AHEAD_DAYS = 90;
const REFRESH_MS = 60_000;

type TabKey = "upcoming" | "past";

const isPast = (m: MeetingView, now: number): boolean => m.status === "ended" || (m.status !== "live" && m.endMs < now);

/** The Meetings page: upcoming and past meetings, join with a code, and a details drawer for each. */
const MeetingsInner: React.FC = () => {
  const users = useChat((s) => s.users);
  const [items, setItems] = useState<MeetingView[] | null>(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState<TabKey>("upcoming");
  const [now, setNow] = useState(() => Date.now());
  const [detailsId, setDetailsId] = useState("");
  const [codeText, setCodeText] = useState("");
  const [codeError, setCodeError] = useState("");
  const [toast, setToast] = useState<{ ok: boolean; text: string } | null>(null);
  const alive = useRef(true);
  const timer = useRef<number | undefined>(undefined);

  const load = useCallback(async () => {
    const t = Date.now();
    try {
      const list = await listMeetings(t - PAST_DAYS * DAY, t + AHEAD_DAYS * DAY);
      if (!alive.current) return;
      setItems(list);
      setNow(t);
      setError("");
    } catch (e) {
      if (alive.current) setError(errText(e, "load your meetings"));
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void load();
    void chat.loadDirectory("");
    const iv = window.setInterval(() => void load(), REFRESH_MS);
    const off = onMeetingFrame((f) => {
      if (!/^meeting\.(created|updated|canceled|started|ended)$/.test(f.t)) return;
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => void load(), 400);
    });
    return () => {
      alive.current = false;
      window.clearInterval(iv);
      window.clearTimeout(timer.current);
      off();
    };
  }, [load]);

  const { upcoming, past } = useMemo(() => {
    const all = (items ?? []).filter((m) => m.status !== "canceled");
    return {
      upcoming: all.filter((m) => !isPast(m, now)).sort((a, b) => a.startMs - b.startMs),
      past: all.filter((m) => isPast(m, now)).sort((a, b) => b.startMs - a.startMs),
    };
  }, [items, now]);

  const shown = tab === "upcoming" ? upcoming : past;
  const details = detailsId ? (items ?? []).find((m) => m.id === detailsId) : undefined;

  const copy = async (m: MeetingView) => {
    try {
      const url = m.joinUrl ?? (await meetingLink(m.id)).joinUrl;
      setToast((await copyText(url)) ? { ok: true, text: `Link to "${m.title}" copied.` } : { ok: false, text: "Couldn't copy the link. Open the details and copy it by hand." });
    } catch (e) {
      setToast({ ok: false, text: errText(e, "get the link") });
    }
  };

  const joinCode = () => {
    const code = codeFromUrl(codeText.trim());
    if (!code) {
      setCodeError("That doesn't look like a meeting code or link. A code looks like ABCD-EFGH-JKMN.");
      return;
    }
    setCodeError("");
    setCodeText("");
    openMeeting({ code });
  };

  return (
    <div className={css.meetings}>
      <div className={css.head}>
        <Title2 as="h1">Meetings</Title2>
        <Button appearance="subtle" icon={<ArrowSync20Regular />} aria-label="Refresh meetings" onClick={() => void load()} />
      </div>

      <form
        className={css.codeBox}
        onSubmit={(e) => {
          e.preventDefault();
          joinCode();
        }}
        aria-label="Join with a code"
      >
        <Input
          className={css.grow}
          value={codeText}
          placeholder="Join with a code or a link"
          aria-label="Meeting code or link"
          onChange={(_, d) => {
            setCodeText(d.value);
            setCodeError("");
          }}
        />
        <Button type="submit" appearance="primary" disabled={!codeText.trim()}>
          Join
        </Button>
      </form>
      {codeError && (
        <MessageBar intent="error" role="alert">
          <MessageBarBody>{codeError}</MessageBarBody>
        </MessageBar>
      )}

      <TabList selectedValue={tab} onTabSelect={(_, d) => setTab(d.value as TabKey)} aria-label="Meetings">
        <Tab value="upcoming">Upcoming{items ? ` (${upcoming.length})` : ""}</Tab>
        <Tab value="past">Past{items ? ` (${past.length})` : ""}</Tab>
      </TabList>

      {toast && (
        <MessageBar intent={toast.ok ? "success" : "error"} role={toast.ok ? "status" : "alert"}>
          <MessageBarBody>{toast.text}</MessageBarBody>
        </MessageBar>
      )}
      {error && (
        <MessageBar intent="error" role="alert">
          <MessageBarBody>{error}</MessageBarBody>
          <Button size="small" onClick={() => void load()}>
            Try again
          </Button>
        </MessageBar>
      )}

      {!items && !error && <Spinner label="Loading your meetings…" className={css.pad} />}
      {items && shown.length === 0 && (
        <Text className={css.empty}>{tab === "upcoming" ? "No upcoming meetings." : `No meetings in the last ${PAST_DAYS} days.`}</Text>
      )}

      <ul className={css.plain} aria-label={tab === "upcoming" ? "Upcoming meetings" : "Past meetings"}>
        {shown.map((m) => {
          const badge = badgeOf(m, now);
          const count = (m.invitees ?? []).length;
          const organizer = m.organizerName || users[m.organizerId]?.name || m.organizerId;
          return (
            <li key={m.id} className={css.meeting}>
              <div className={css.meetingMain}>
                <div className={css.meetingTitle}>
                  <strong>{m.title}</strong>
                  <StatusChip badge={badge} />
                </div>
                <Text className={css.muted} block>
                  {fmtWhen(m.startMs, m.endMs, m.timeZone)}
                </Text>
                <Text className={css.muted} block>
                  Organiser: {organizer} · {count} {count === 1 ? "invitee" : "invitees"}
                </Text>
              </div>
              <div className={css.meetingActions}>
                {badge !== "ended" && (
                  <Button appearance="primary" icon={<Video20Regular />} aria-label={`Join ${m.title}`} onClick={() => openMeeting({ meetingId: m.id })}>
                    Join
                  </Button>
                )}
                <Button icon={<Copy20Regular />} aria-label={`Copy link to ${m.title}`} onClick={() => void copy(m)}>
                  Copy link
                </Button>
                <Button icon={<Info20Regular />} aria-label={`Details of ${m.title}`} onClick={() => setDetailsId(m.id)}>
                  Details
                </Button>
              </div>
            </li>
          );
        })}
      </ul>

      {details && <MeetingDetails key={details.id} summary={details} onClose={() => setDetailsId("")} onChanged={() => void load()} />}
    </div>
  );
};

const MeetingsPage: React.FC = () => (
  <FluentProvider theme={webLightTheme} className={css.pageWrap}>
    <MeetingsInner />
  </FluentProvider>
);

export default MeetingsPage;
